/**
 * Mulch & Stuff Landscape Material Project Planner
 * Centralized controlled configuration foundation.
 *
 * Phase 3 production material configuration.
 * Contains the currently authorized production planner material catalog and
 * authoritative estimated-weight, purchase capability, and product-link data.
 */
(function (root) {
  'use strict';

  const configuration = Object.freeze({
    revision: 'purchase-additional-materials-1',
    validationState: 'production-materials',
    materials: Object.freeze([
      Object.freeze({
        id: 'gold-mulch',
        label: 'Gold Mulch',
        production: true,
        productInfoUrl: '/products/gold-mulch-premium-landscape-ground-cover-ormond-beach',
        displayOrder: 1,
        category: 'Mulch',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: false,
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 550,
          maximumPoundsPerCubicYard: 800
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'red-mulch',
        label: 'Red Mulch',
        production: true,
        productInfoUrl: '/products/premium-red-mulch-landscaping-gardens-flower-beds',
        displayOrder: 2,
        category: 'Mulch',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: false,
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 550,
          maximumPoundsPerCubicYard: 800
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'brown-mulch',
        label: 'Brown Mulch',
        production: true,
        productInfoUrl: '/products/premium-brown-chocolate-mulch-landscaping-gardens-flower-beds',
        displayOrder: 3,
        category: 'Mulch',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: false,
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 550,
          maximumPoundsPerCubicYard: 800
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'black-mulch',
        label: 'Black Mulch',
        production: true,
        productInfoUrl: '/products/premium-black-mulch-landscaping-gardens-flower-beds',
        displayOrder: 4,
        category: 'Mulch',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: false,
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 550,
          maximumPoundsPerCubicYard: 800
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'pine-bark-mini-nuggets',
        label: 'Pine Bark Mini Nuggets',
        production: true,
        productInfoUrl: '/products/premium-pine-bark-mini-nuggets-landscaping-gardens-flower-beds',
        displayOrder: 5,
        category: 'Pine Bark',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: false,
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 300,
          maximumPoundsPerCubicYard: 400
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'fill-dirt',
        label: 'Fill Dirt',
        production: true,
        productInfoUrl: '/products/clean-fill-dirt-landscaping-gardening-leveling-construction',
        displayOrder: 6,
        category: 'Soil',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 2500,
          maximumPoundsPerCubicYard: 2800
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'top-soil',
        label: 'Top Soil',
        production: true,
        productInfoUrl: '/products/premium-top-soil-landscaping-gardens-lawns-mulch-and-stuff',
        displayOrder: 7,
        category: 'Soil',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 1900,
          maximumPoundsPerCubicYard: 2100
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'grey-granite',
        label: 'Grey Granite',
        production: true,
        productInfoUrl: '/products/grey-granite-salt-and-pepper-1-to-1-5-inch-landscaping-stone',
        displayOrder: 8,
        category: 'Stone',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        weight: Object.freeze({
          type: 'single',
          poundsPerCubicYard: 2700
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'brown-river-rock',
        label: 'Brown River Rock',
        production: true,
        productInfoUrl: '/products/brown-river-round-rock-1-to-1-5-inch-landscaping-gardens-flower-beds',
        displayOrder: 9,
        category: 'Stone',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        weight: Object.freeze({
          type: 'single',
          poundsPerCubicYard: 2700
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'white-river-rock',
        label: 'White River Rock',
        production: true,
        productInfoUrl: '/products/white-river-rock-1-inch-to-1-2-inch',
        displayOrder: 10,
        category: 'Stone',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        weight: Object.freeze({
          type: 'single',
          poundsPerCubicYard: 2700
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: '57-limestone',
        label: '#57 Limestone',
        production: true,
        productInfoUrl: '/products/57-limestone-gravel',
        displayOrder: 11,
        category: 'Stone',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 2000,
          maximumPoundsPerCubicYard: 2300
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'white-mason-fine-sand',
        label: 'White Mason Fine Sand',
        production: true,
        productInfoUrl: '/products/white-mason-sand-premium-construction-landscaping-mulch-and-stuff',
        displayOrder: 12,
        category: 'Sand',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 2500,
          maximumPoundsPerCubicYard: 2800
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'white-coarse-sand',
        label: 'White Coarse Sand',
        production: true,
        productInfoUrl: '/products/white-coarse-sand-concrete-pavers-drainage-landscaping',
        displayOrder: 13,
        category: 'Sand',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 2500,
          maximumPoundsPerCubicYard: 2800
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'paver-base',
        label: 'Paver Base',
        production: true,
        productInfoUrl: '/products/paver-base-type-1-grade-a-premium-recycled-concrete',
        displayOrder: 14,
        category: 'Base Material',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 2700,
          maximumPoundsPerCubicYard: 2900
        }),
        depthPresets: Object.freeze([2, 3, 4])
      }),
      Object.freeze({
        id: 'road-base',
        label: 'Road Base',
        production: true,
        productInfoUrl: '/products/road-base-crushed-concrete-bulk-for-landscaping-driveways-and-construction',
        displayOrder: 15,
        category: 'Base Material',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: true,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        depthPresets: Object.freeze([2, 3, 4]),
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 2600,
          maximumPoundsPerCubicYard: 2800
        })
      }),
      Object.freeze({
        id: 'red-sand',
        label: 'Red Sand',
        selectorLabel: 'Red Sand (currently only available in bags)',
        production: true,
        productInfoUrl: '/products/premium-red-sand-landscaping-paver-base-mulch-and-stuff',
        displayOrder: 16,
        category: 'Sand',
        plannerVisible: true,
        availability: 'available',
        bulkCapable: false,
        bagCapable: true,
        bagConversion: Object.freeze({type: 'volume', cubicFeetPerBag: 0.5}),
        weight: Object.freeze({
          type: 'range',
          minimumPoundsPerCubicYard: 2500,
          maximumPoundsPerCubicYard: 2700
        }),
        depthPresets: Object.freeze([2, 3, 4])
      })
    ]),
    quickDepthPresets: Object.freeze([]),
    // Product paths supplied and verified by Control. Purchase capability is
    // independent of availability status; changing either requires only data edits.
    ancillaryRules: Object.freeze({
      fabric: Object.freeze({widthFeet: 3, defaultAllowancePercent: 15,
        productInfoUrl: '/products/6-oz-non-woven-landscape-fabric-20-year-weed-barrier'}),
      staples: Object.freeze({defaultSpacingFeet: 8,
        productInfoUrl: '/products/6-inch-heavy-duty-landscape-staples-galvanized-weed-barrier-pins-for-garden-soil'}),
      alphaEdge: Object.freeze({stickLengthFeet: 20,
        productInfoUrl: '/products/alpha-edge%C2%AE-landscape-edging-20-ft-self-anchoring-professional-garden-border-durable-uv-stabilized-polymer'}),
      corners: Object.freeze({
        productInfoUrl: '/products/alpha-edge-90-degree-landscape-edging-connector-corner-piece'})
    })
  });

  if (root) {
    root.MSProjectPlannerConfig = configuration;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = configuration;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
